// ══════════════════════════════════════════════════════════════════
//  HomePage.jsx
// ══════════════════════════════════════════════════════════════════
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ListingCard from '../components/listings/ListingCard';
import SecondFactorPrompt from '../components/SecondFactorPrompt';
import SocialLogin from '../components/SocialLogin';
import { useSEO } from '../hooks/useSEO';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

export function HomePage() {
  useSEO({});
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const [listings,    setListings]    = useState({ rent:[], auction:[], sale:[], barter:[], free:[] });
  const [featured,    setFeatured]    = useState([]);
  const [trending,    setTrending]    = useState([]);
  const [promoted,    setPromoted]    = useState([]);
  const [categoryHighlights, setCategoryHighlights] = useState({});
  const [newItems,    setNewItems]    = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [newItemsLabel, setNewItemsLabel] = useState('New This Week');
  const [trendingView, setTrendingView] = useState('trending'); // 'trending' or 'new'
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [query,       setQuery]       = useState('');
  const [activeCat,   setActiveCat]   = useState('All');
  const [categories,  setCategories]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestName, setSuggestName] = useState('');
  const [suggestIcon, setSuggestIcon] = useState('📦');
  const [suggestDone, setSuggestDone] = useState(false);
  const [suggestErr,  setSuggestErr]  = useState('');

  const ICONS = ['📦','🛠️','🎯','🧸','🌿','🔬','🎭','🏠','🚀','💎','🌊','🎪','🔮','🧲','⚡','🌺','🦋','🎵','🏺','⚙️'];

  useEffect(() => {
    const majorCategories = ['electronics', 'home-garden', 'fashion', 'sports', 'collectibles'];

    // Try to get user's location for location-based new items
    let userLat, userLng, cityName;

    if (user?.lat && user?.lng) {
      // Use logged-in user's saved location
      userLat = user.lat;
      userLng = user.lng;
      cityName = user.locationCity;
    }

    // Load core data first (10 requests)
    const coreRequests = [
      API.get('/listings?type=rent&limit=4'),
      API.get('/listings?type=auction&limit=4'),
      API.get('/listings?type=sale&limit=4'),
      API.get('/barters/listings?limit=4'),
      API.get('/free/listings?limit=4'),
      API.get('/search/featured?limit=8'),
      API.get('/search/trending?limit=8'),
      API.get('/categories'),
      API.get('/promotions/active?limit=12'),
    ];

    // Add location-based new items if user has location
    let newItemsUrl = '/search/new?limit=8&days=7';
    if (userLat && userLng) {
      newItemsUrl += `&lat=${userLat}&lng=${userLng}&distance=25`;
    }
    coreRequests.push(API.get(newItemsUrl));

    Promise.allSettled(coreRequests).then((results) => {
      const [r, a, s, b, fr, f, t, cats, promo, n] = results;
      setListings({
        rent: r.status === 'fulfilled' ? r.value.data.listings : [],
        auction: a.status === 'fulfilled' ? a.value.data.listings : [],
        sale: s.status === 'fulfilled' ? s.value.data.listings : [],
        barter: b.status === 'fulfilled' ? b.value.data.listings : [],
        free: fr.status === 'fulfilled' ? fr.value.data.listings : []
      });
      setFeatured(f.status === 'fulfilled' ? f.value.data.results || [] : []);
      setTrending(t.status === 'fulfilled' ? t.value.data.results || [] : []);
      setPromoted(promo.status === 'fulfilled' ? promo.value.data.promotions || [] : []);
      setNewItems(n.status === 'fulfilled' ? n.value.data.results || [] : []);

      if (userLat && userLng) {
        setUserLocation({ lat: userLat, lng: userLng });
        setNewItemsLabel(`New in ${cityName || 'Your Area'}`);
      }

      const catData = cats.status === 'fulfilled' ? cats.value.data.categories : [];
      setCategories(catData);
      setLoading(false);

      // Load category highlights sequentially (staggered 400ms apart) to avoid rate limiting
      let delayMs = 0;
      majorCategories.forEach((catSlug) => {
        setTimeout(async () => {
          try {
            const { data } = await API.get(`/search/trending?category=${catSlug}&limit=4`);
            const items = data.results || [];
            const catInfo = catData.find(c => c.slug === catSlug);
            if (items.length > 0 && catInfo) {
              setCategoryHighlights(prev => ({ ...prev, [catSlug]: { ...catInfo, items } }));
            }
          } catch (err) {
            console.error(`Failed to load category ${catSlug}:`, err);
          }
        }, delayMs);
        delayMs += 400;
      });
    });
  }, []);

  // Set initial category when highlights load
  useEffect(() => {
    if (!selectedCatId && Object.keys(categoryHighlights).length > 0) {
      const firstCat = Object.keys(categoryHighlights)[0];
      setSelectedCatId(firstCat);
    }
  }, [categoryHighlights]);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'superadmin') {
      API.get('/admin/suggestions')
        .then(r => setSuggestions(r.data.suggestions || []))
        .catch(() => {});
    }
  }, [user]);

  const handlePromote = async (s) => {
    try {
      await API.post(`/categories/${s.id}/promote`);
      setSuggestions(prev => prev.filter(x => x.id !== s.id));
      API.get('/categories').then(r => setCategories(r.data.categories));
    } catch (err) {
      alert(err.response?.data?.error || 'Promote failed');
    }
  };

  const handleSuggest = async () => {
    if (!suggestName.trim()) return;
    setSuggestErr('');
    try {
      await API.post('/categories/suggest', { label: suggestName.trim(), icon: suggestIcon });
      setSuggestDone(true);
      setSuggestName('');
    } catch (err) {
      setSuggestErr(err.response?.data?.error || 'Could not submit — please sign in first');
    }
  };

  const handleSearch = () => {
    if (query.trim()) navigate(`/browse?q=${encodeURIComponent(query)}`);
  };

  const stats = [{ num:'16k+',label:'Listings'},{ num:'4.9★',label:'Avg Rating'},{ num:'100%',label:'Secure Escrow'},{ num:'3,800+',label:'Happy Users'}];
  const hiw = [
    { icon:'🔍', num:'01', title:'Find what you need', desc:'Search thousands of local listings across rentals, auctions, and sales.' },
    { icon:'🔒', num:'02', title:'Pay safely via escrow', desc:"Payment held securely — card or crypto. Released only when you're satisfied." },
    { icon:'📍', num:'03', title:'Meet at a safe spot', desc:'Owners set public meetup locations. Safe, neutral handovers every time.' },
    { icon:'✅', num:'04', title:'Complete the deal', desc:'Confirm receipt, funds release, deposit returned. Leave a review.' },
  ];

  return (
    <div>
      {/* Hero */}
      <div className="hero">
        <div className="hero-content">
          <div className="hero-eyebrow"><span className="hero-pulse"/>Now live in your area</div>
          <h1 className="hero-h1">Rent it. <em>Bid on it.</em> Buy it.</h1>
          <p className="hero-sub">The marketplace built on trust. Borrow, discover, and buy quality items from real people in your community.</p>
          <div className="hero-cta">
            <button className="btn btn-cherry btn-lg" onClick={() => navigate('/browse')}>🔍 Browse Listings</button>
            <button className="btn btn-lg" style={{ background:'transparent',color:'white',border:'1.5px solid rgba(255,255,255,.4)' }} onClick={() => navigate('/list')}>📋 List an Item</button>
          </div>
          <div className="hero-search">
            <input className="hero-search-input" placeholder="Search cameras, tools, furniture..." value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSearch()}/>
            <button className="hero-search-btn" onClick={handleSearch}>Search</button>
          </div>
          <div className="hero-stats">
            {stats.map(s=><div key={s.label} className="hero-stat"><div className="hero-stat-num">{s.num}</div><div className="hero-stat-label">{s.label}</div></div>)}
          </div>
        </div>
      </div>

      {/* Category strip */}
      <div className="cat-strip">
        <div className="cat-list" style={{ padding:'0 48px' }}>
          {['All',...categories.map(c=>c.label)].map(cat=>(
            <button key={cat} className={`cat-btn ${activeCat===cat?'active':''}`} onClick={()=>setActiveCat(cat)}>
              {categories.find(c=>c.label===cat)?.icon||'✨'} {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Admin: pending category suggestions banner */}
      {suggestions.length > 0 && (
        <div style={{ maxWidth:1280, margin:'24px auto 0', padding:'0 48px' }}>
          <div style={{ background:'linear-gradient(135deg,#1A1A1A,#2C2C2C)', borderRadius:14, padding:'18px 24px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
            <span style={{ fontSize:24 }}>🏷️</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'white', marginBottom:10 }}>
                Pending category suggestions
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {suggestions.map(s => (
                  <div key={s.id} style={{ background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.12)', borderRadius:8, padding:'6px 12px', display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:16 }}>{s.icon}</span>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:'white' }}>{s.label}</div>
                      <div style={{ fontSize:10, color:'#888' }}>{s.vote_count} vote{s.vote_count!==1?'s':''}</div>
                    </div>
                    <button
                      onClick={() => handlePromote(s)}
                      style={{ padding:'3px 10px', borderRadius:5, border:'none', background:'#2D7A3A', color:'white', fontSize:10, fontWeight:700, cursor:'pointer' }}>
                      ✅ Promote
                    </button>
                    <button
                      onClick={() => setSuggestions(prev => prev.filter(x => x.id !== s.id))}
                      style={{ padding:'3px 7px', borderRadius:5, border:'none', background:'rgba(255,255,255,.1)', color:'#aaa', fontSize:10, cursor:'pointer' }}>
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Featured, Trending & New Sections */}
      <div style={{ maxWidth:1280,margin:'0 auto',padding:'56px 48px 0' }}>

        {/* Promoted Listings (VIP, Premium, Basic tiers) */}
        {promoted.length > 0 && (
          <section style={{ paddingBottom:56 }}>
            <div style={{ display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:24 }}>
              <div><div className="section-eyebrow">⭐ Premium Listings</div><h2 className="section-title">Promoted by Sellers</h2></div>
            </div>
            <div className="listings-grid">
              {promoted.slice(0,8).map((l,i)=>{
                const tierBadge = { vip:'🥇 VIP', premium:'🥈 Premium', basic:'🥉 Basic' }[l.tier];
                return (
                  <div key={l.id} style={{ position:'relative' }}>
                    <ListingCard listing={l} delay={i*.07}/>
                    <div style={{
                      position:'absolute',
                      top:8,
                      left:8,
                      background:'linear-gradient(135deg, #FFD700, #FFA500)',
                      color:'#1C1C1C',
                      padding:'4px 10px',
                      borderRadius:20,
                      fontSize:11,
                      fontWeight:700,
                      boxShadow:'0 2px 8px rgba(0,0,0,0.15)'
                    }}>
                      {tierBadge}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Featured (Admin-curated) */}
        {featured.length > 0 && (
          <section style={{ paddingBottom:56 }}>
            <div style={{ display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:24 }}>
              <div><div className="section-eyebrow">⭐ Editor's Picks</div><h2 className="section-title">Featured Items</h2></div>
              <a style={{ fontSize:14,fontWeight:600,color:'var(--cherry)',cursor:'pointer' }} onClick={()=>navigate('/browse')}>Browse all →</a>
            </div>
            <div className="listings-grid">
              {featured.slice(0,8).map((l,i)=><ListingCard key={l.id} listing={l} delay={i*.07}/>)}
            </div>
          </section>
        )}

        {/* Trending vs New Toggle */}
        {(trending.length > 0 || newItems.length > 0) && (
          <section style={{ paddingBottom:56 }}>
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24,flexWrap:'wrap',gap:16 }}>
              <div style={{ display:'flex',alignItems:'center',gap:16 }}>
                <div><div className="section-eyebrow">{trendingView === 'trending' ? '🔥 Hot right now' : '✨ Just posted'}</div><h2 className="section-title">{trendingView === 'trending' ? 'Trending Today' : newItemsLabel}</h2></div>
                <div style={{ display:'flex',gap:4,background:'var(--cream)',borderRadius:8,padding:4 }}>
                  <button onClick={() => setTrendingView('trending')} style={{ padding:'6px 16px',borderRadius:6,border:'none',background:trendingView==='trending'?'white':'transparent',color:trendingView==='trending'?'var(--cherry)':'var(--text-soft)',cursor:'pointer',fontWeight:600,fontSize:13,transition:'all .2s' }}>🔥 Trending</button>
                  <button onClick={() => setTrendingView('new')} style={{ padding:'6px 16px',borderRadius:6,border:'none',background:trendingView==='new'?'white':'transparent',color:trendingView==='new'?'var(--cherry)':'var(--text-soft)',cursor:'pointer',fontWeight:600,fontSize:13,transition:'all .2s' }}>✨ New</button>
                </div>
              </div>
              <a style={{ fontSize:14,fontWeight:600,color:'var(--cherry)',cursor:'pointer' }} onClick={()=>navigate(trendingView==='trending'?'/browse?sort=trending':'/browse?daysPosted=7')}>View all →</a>
            </div>
            <div className="listings-grid">
              {(trendingView === 'trending' ? trending : newItems).slice(0,8).map((l,i)=><ListingCard key={l.id} listing={l} delay={i*.07}/>)}
            </div>
          </section>
        )}

        {/* Category Highlights - TABBED */}
        {Object.keys(categoryHighlights).length > 0 && (
          <section style={{ paddingBottom:56 }}>
            <div style={{ marginBottom:24 }}>
              <div style={{ marginBottom:16 }}><div className="section-eyebrow">🌟 Hot in Categories</div><h2 className="section-title">Trending by Category</h2></div>
              <div style={{ display:'flex',gap:8,overflowX:'auto',paddingBottom:12 }}>
                {Object.entries(categoryHighlights).map(([slug, cat]) => (
                  <button
                    key={slug}
                    onClick={() => setSelectedCatId(slug)}
                    style={{
                      padding:'8px 16px',
                      borderRadius:8,
                      border:'none',
                      background:selectedCatId===slug?'var(--cherry)':'white',
                      color:selectedCatId===slug?'white':'var(--text-mid)',
                      cursor:'pointer',
                      fontWeight:600,
                      fontSize:13,
                      whiteSpace:'nowrap',
                      transition:'all .2s',
                      flexShrink:0
                    }}
                  >
                    {cat.icon} {cat.label}
                  </button>
                ))}
              </div>
            </div>
            {selectedCatId && categoryHighlights[selectedCatId] && (
              <div>
                <h3 style={{ fontSize:16,fontWeight:700,marginBottom:16 }}>
                  {categoryHighlights[selectedCatId].icon} Trending in {categoryHighlights[selectedCatId].label}
                </h3>
                <div className="listings-grid">
                  {categoryHighlights[selectedCatId].items.map((l,i)=><ListingCard key={l.id} listing={l} delay={i*.07}/>)}
                </div>
              </div>
            )}
          </section>
        )}

      </div>

      {/* Listing sections */}
      <div style={{ maxWidth:1280,margin:'0 auto',padding:'0 48px' }}>
        {[
          { eyebrow:'Rent it',   title:'Top Rentals Near You',  items:listings.rent,    link:'View all rentals',  type:'rent' },
          { eyebrow:'Bid on it', title:'Live Auctions',          items:listings.auction, link:'View all auctions', type:'auction' },
          { eyebrow:'Buy it',    title:'For Sale Near You',      items:listings.sale,    link:'View all listings', type:'sale' },
          { eyebrow:'Trade it',  title:'Barter Trades',          items:listings.barter,  link:'View all trades',   type:'barter' },
          { eyebrow:'Take it',   title:'Free Items',             items:listings.free,    link:'View all free',    type:'free' },
        ].map(({ eyebrow, title, items, link, type }) => items.length > 0 && (
          <section key={type} style={{ paddingTop:56 }}>
            <div style={{ display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:24 }}>
              <div><div className="section-eyebrow">{eyebrow}</div><h2 className="section-title">{title}</h2></div>
              <a style={{ fontSize:14,fontWeight:600,color:'var(--cherry)',cursor:'pointer' }} onClick={()=>navigate(type==='barter'?'/barter':type==='free'?'/free':`/browse?type=${type}`)}>{link} →</a>
            </div>
            <div className="listings-grid">
              {items.map((l,i)=><ListingCard key={l.id} listing={l} delay={i*.07}/>)}
            </div>
          </section>
        ))}
      </div>

      {/* How it works */}
      <div className="hiw" style={{ marginTop:56 }}>
        <div style={{ maxWidth:1280,margin:'0 auto' }}>
          <div style={{ textAlign:'center',marginBottom:48 }}>
            <div style={{ fontSize:11,fontWeight:700,letterSpacing:'3px',textTransform:'uppercase',color:'var(--cherry)',marginBottom:12 }}>Simple by design</div>
            <h2 style={{ fontFamily:"'Playfair Display',serif",fontSize:'clamp(28px,4vw,44px)',fontWeight:700,color:'white',letterSpacing:'-1px' }}>How Cherrypkd works</h2>
          </div>
          <div className="hiw-grid">
            {hiw.map((s,i)=>(
              <div key={i} className="hiw-card" data-num={s.num}>
                <div style={{ width:48,height:48,background:'var(--cherry)',borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,marginBottom:18 }}>{s.icon}</div>
                <h3 style={{ fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:'white',marginBottom:10 }}>{s.title}</h3>
                <p style={{ fontSize:13,color:'#888',lineHeight:1.72 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="trust-strip">
        <div className="trust-items">
          {['🔒 Secure escrow','✅ ID verified users','🛡️ Damage deposits','⭐ Rated reviews','📍 Safe meetups','💳 Card & 🪙 Crypto'].map((t,i)=>(
            <div key={i} style={{ display:'flex',alignItems:'center',gap:0 }}>
              {i>0&&<div style={{ width:1,height:20,background:'rgba(255,255,255,.25)',marginRight:36 }}/>}
              <div className="trust-item">{t}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Suggest a category */}
      <div style={{ maxWidth:1280,margin:'0 auto',padding:'56px 48px 0' }}>
        <div style={{ background:'#F5F0EB', border:'1.5px dashed var(--cherry)', borderRadius:16, padding:'32px 36px' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:24, flexWrap:'wrap' }}>
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:6 }}>
                ✨ Don't see your category?
              </h3>
              <p style={{ fontSize:14, color:'var(--text-mid)', lineHeight:1.65, maxWidth:460 }}>
                Suggest a new one. Our team reviews all suggestions and adds them when they reach enough demand.
              </p>
            </div>
            {!showSuggest && !suggestDone && (
              <button className="btn btn-cherry" onClick={() => setShowSuggest(true)}>
                + Suggest a Category
              </button>
            )}
          </div>

          {/* Suggest form */}
          {showSuggest && !suggestDone && (
            <div style={{ marginTop:24, paddingTop:24, borderTop:'1px solid var(--border)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">Category Name *</label>
                  <input
                    className="form-input"
                    style={{ background:'white', borderStyle:'dashed', borderColor:'var(--cherry)' }}
                    placeholder="e.g. 3D Printing & Maker"
                    value={suggestName}
                    onChange={e => setSuggestName(e.target.value)}
                    maxLength={50}
                  />
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">What kind of items?</label>
                  <input
                    className="form-input"
                    placeholder="e.g. 3D printers, filament, maker kits"
                  />
                </div>
              </div>

              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:8 }}>Choose an icon</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {ICONS.map(ic => (
                    <button
                      key={ic}
                      onClick={() => setSuggestIcon(ic)}
                      style={{ width:36, height:36, borderRadius:8, border:`1.5px solid ${suggestIcon===ic?'var(--cherry)':'var(--border)'}`, background:suggestIcon===ic?'var(--cherry-light)':'white', fontSize:18, cursor:'pointer', transition:'all .15s' }}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>

              {suggestErr && <div className="alert alert-error" style={{ marginBottom:12 }}>{suggestErr}</div>}

              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <button className="btn btn-cherry" onClick={handleSuggest} disabled={!suggestName.trim()}>
                  Submit Suggestion
                </button>
                <button className="btn btn-ghost" onClick={() => { setShowSuggest(false); setSuggestName(''); setSuggestErr(''); }}>
                  Cancel
                </button>
                {suggestName && (
                  <div style={{ fontSize:13, color:'var(--text-mid)' }}>
                    Preview: <strong>{suggestIcon} {suggestName}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Success state */}
          {suggestDone && (
            <div style={{ marginTop:20, background:'var(--green-light)', border:'1.5px solid rgba(45,122,58,.2)', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:22 }}>✅</span>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:'var(--green)', marginBottom:2 }}>
                  {suggestIcon} "{suggestName || 'Your suggestion'}" submitted for review!
                </div>
                <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                  We'll add it as an official category once it gets enough demand. Thank you!
                </div>
              </div>
              <button
                className="btn btn-ghost btn-sm"
                style={{ marginLeft:'auto' }}
                onClick={() => { setSuggestDone(false); setShowSuggest(false); setSuggestName(''); setSuggestIcon('📦'); }}>
                Suggest another
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sell banner */}
      <div style={{ maxWidth:1280,margin:'0 auto',padding:'48px 48px 64px' }}>
        <div style={{ background:'#F5F0EB',borderRadius:20,border:'1px solid var(--border)',padding:'52px 56px',display:'grid',gridTemplateColumns:'1fr auto',gap:56,alignItems:'center',position:'relative',overflow:'hidden' }}>
          <div style={{ position:'absolute',right:260,top:-20,fontSize:160,opacity:.05,transform:'rotate(-15deg)',pointerEvents:'none' }}>🍒</div>
          <div>
            <h2 style={{ fontFamily:"'Playfair Display',serif",fontSize:'clamp(26px,3vw,36px)',fontWeight:700,letterSpacing:'-.5px',marginBottom:12 }}>
              Got stuff? <em style={{ color:'var(--cherry)',fontStyle:'italic' }}>Make money.</em>
            </h2>
            <p style={{ fontSize:15,color:'var(--text-mid)',lineHeight:1.65,maxWidth:480,marginBottom:20 }}>List for rent, auction, or sale in under 5 minutes. Free to list — we only take 10% on completed deals.</p>
            <div style={{ display:'flex',gap:20,flexWrap:'wrap' }}>
              {['Free to list','10% on completed deals','Damage deposits built in','Payouts within 48hrs'].map(p=>(
                <div key={p} style={{ display:'flex',alignItems:'center',gap:6,fontSize:13,fontWeight:600,color:'var(--text-mid)' }}>
                  <div style={{ width:6,height:6,background:'var(--cherry)',borderRadius:'50%' }}/>{p}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display:'flex',flexDirection:'column',gap:10,alignItems:'center',flexShrink:0 }}>
            <button className="btn btn-cherry btn-lg" onClick={()=>navigate('/list')}>Start listing today</button>
            <span style={{ fontSize:11,color:'var(--text-soft)' }}>No subscription · No upfront fees</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  LoginPage.jsx
// ══════════════════════════════════════════════════════════════════
export function LoginPage() {
  useSEO({ title: 'Sign in' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, updateUser } = useAuth();
  const [form, setForm] = useState({ email:'', password:'' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requiresSecondFactor, setRequiresSecondFactor] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);

  // Handle OAuth redirect callback (Google sets ?token=<jwt>&provider=google)
  useEffect(() => {
    const token = searchParams.get('token');
    const oauthError = searchParams.get('error');
    if (oauthError) { setError('Sign-in failed — please try again'); return; }
    if (token) {
      localStorage.setItem('accessToken', token);
      API.get('/auth/me').then(r => {
        updateUser(r.data.user);
        const redirect = searchParams.get('redirect') || '/dashboard';
        navigate(redirect, { replace: true });
      }).catch(() => setError('Social sign-in failed'));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await API.post('/auth/login', { email: form.email, password: form.password });

      if (res.data.requiresSecondFactor) {
        // User has 2FA enabled, show second factor prompt
        setTempToken(res.data.tempToken);
        setTwoFaEnabled(res.data.twoFaEnabled);
        setPasskeyEnabled(res.data.passkeyEnabled);
        setRequiresSecondFactor(true);
      } else {
        // No 2FA, proceed normally
        await login(form.email, form.password);
        const redirect = searchParams.get('redirect') || '/dashboard';
        navigate(redirect);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleSecondFactorSuccess = (data) => {
    // Store tokens and user data
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    updateUser(data.user);
    const redirect = searchParams.get('redirect') || '/dashboard';
    navigate(redirect);
  };

  const handleSecondFactorCancel = () => {
    setRequiresSecondFactor(false);
    setTempToken('');
    setForm({ email: '', password: '' });
  };

  if (requiresSecondFactor) {
    return (
      <SecondFactorPrompt
        tempToken={tempToken}
        email={form.email}
        twoFaEnabled={twoFaEnabled}
        passkeyEnabled={passkeyEnabled}
        onSuccess={handleSecondFactorSuccess}
        onCancel={handleSecondFactorCancel}
      />
    );
  }

  return (
    <div style={{ minHeight:'calc(100vh - 68px)',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)',padding:24 }}>
      <div style={{ background:'white',borderRadius:20,padding:48,width:'100%',maxWidth:440,boxShadow:'var(--shadow-lg)' }}>
        <div style={{ textAlign:'center',marginBottom:32 }}>
          <div style={{ fontSize:36,marginBottom:12 }}>🍒</div>
          <h1 style={{ fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700,marginBottom:6 }}>Welcome back</h1>
          <p style={{ fontSize:14,color:'var(--text-soft)' }}>Sign in to your Cherrypkd account</p>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom:16 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" placeholder="you@example.com" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" placeholder="••••••••" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} required />
            <div style={{ textAlign:'right',marginTop:4 }}>
              <a style={{ fontSize:12,color:'var(--cherry)',cursor:'pointer' }} onClick={()=>navigate('/forgot-password')}>Forgot password?</a>
            </div>
          </div>
          <button type="submit" className="btn btn-cherry btn-full btn-lg" disabled={loading}>
            {loading ? <><span className="spinner"/> Signing in...</> : 'Sign in'}
          </button>
        </form>

        <SocialLogin
          onSuccess={(data) => {
            localStorage.setItem('accessToken', data.accessToken);
            updateUser(data.user);
            navigate(searchParams.get('redirect') || '/dashboard');
          }}
          onError={setError}
        />

        <div style={{ textAlign:'center',marginTop:20,fontSize:14,color:'var(--text-soft)' }}>
          Don't have an account?{' '}
          <a style={{ color:'var(--cherry)',fontWeight:600,cursor:'pointer' }} onClick={()=>{const redirect=searchParams.get('redirect');navigate(redirect?`/signup?redirect=${encodeURIComponent(redirect)}`:'/signup')}}>Sign up free</a>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  SignupPage.jsx
// ══════════════════════════════════════════════════════════════════
export function SignupPage() {
  useSEO({ title: 'Create account' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, updateUser } = useAuth();
  const [form, setForm] = useState({ firstName:'',lastName:'',username:'',email:'',password:'',confirmPassword:'',locationCity:'',locationState:'',locationZip:'' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.firstName) e.firstName = 'Required';
    if (!form.lastName)  e.lastName  = 'Required';
    if (!form.email)     e.email     = 'Required';
    if (form.password.length < 8) e.password = 'Min 8 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords don\'t match';
    if (form.username && form.username.length < 3) e.username = 'Min 3 characters';
    setErrors(e); return !Object.keys(e).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await signup({ firstName:form.firstName, lastName:form.lastName, username:form.username, email:form.email, password:form.password, locationCity:form.locationCity, locationState:form.locationState, locationZip:form.locationZip });
      const redirect = searchParams.get('redirect') || '/dashboard';
      navigate(redirect);
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Signup failed' });
    } finally { setLoading(false); }
  };

  const f = (name) => ({
    className: `form-input ${errors[name] ? 'error' : ''}`,
    value: form[name],
    onChange: e => setForm(p => ({ ...p, [name]: e.target.value })),
  });

  return (
    <div style={{ minHeight:'calc(100vh - 68px)',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)',padding:24 }}>
      <div style={{ background:'white',borderRadius:20,padding:48,width:'100%',maxWidth:600,boxShadow:'var(--shadow-lg)' }}>
        <div style={{ textAlign:'center',marginBottom:32 }}>
          <div style={{ fontSize:36,marginBottom:12 }}>🍒</div>
          <h1 style={{ fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700,marginBottom:6 }}>Create your account</h1>
          <p style={{ fontSize:14,color:'var(--text-soft)' }}>Free to join. Start renting, bidding and selling today.</p>
        </div>

        {/* Free Account Benefits */}
        <div style={{ background:'#F1F8E9',borderRadius:12,padding:16,marginBottom:24 }}>
          <div style={{ fontSize:12,fontWeight:700,color:'#4CAF50',marginBottom:8,textTransform:'uppercase' }}>✓ Included with free account</div>
          <ul style={{ fontSize:13,color:'var(--text-mid)',margin:0,paddingLeft:20,lineHeight:1.8 }}>
            <li>Browse all items (rent, buy, auction)</li>
            <li>Claim free items</li>
            <li>Make purchases & rentals</li>
            <li>Bid on auctions</li>
          </ul>
          <div style={{ fontSize:12,fontWeight:700,color:'var(--cherry)',marginTop:12,marginBottom:0,textTransform:'uppercase' }}>💳 Optional: Upgrade to trade ($20/month or $10 per trade)</div>
        </div>

        {errors.submit && <div className="alert alert-error" style={{ marginBottom:16 }}>{errors.submit}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
            <div className="form-group">
              <label className="form-label">First Name<span className="required"> *</span></label>
              <input {...f('firstName')} placeholder="John" />
              {errors.firstName && <div className="form-error">{errors.firstName}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Last Name<span className="required"> *</span></label>
              <input {...f('lastName')} placeholder="Smith" />
              {errors.lastName && <div className="form-error">{errors.lastName}</div>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Username <span style={{ color:'var(--text-soft)', fontSize:12 }}>(optional)</span></label>
            <input {...f('username')} placeholder="johnsmith123" />
            {errors.username && <div className="form-error">{errors.username}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Email<span className="required"> *</span></label>
            <input {...f('email')} type="email" placeholder="you@example.com" />
            {errors.email && <div className="form-error">{errors.email}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Password<span className="required"> *</span></label>
            <input {...f('password')} type="password" placeholder="Min 8 characters" />
            {errors.password && <div className="form-error">{errors.password}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Confirm Password<span className="required"> *</span></label>
            <input {...f('confirmPassword')} type="password" placeholder="Repeat password" />
            {errors.confirmPassword && <div className="form-error">{errors.confirmPassword}</div>}
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12 }}>
            <div className="form-group">
              <label className="form-label">City</label>
              <input {...f('locationCity')} placeholder="Matamoras" />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input {...f('locationState')} placeholder="PA" />
            </div>
            <div className="form-group">
              <label className="form-label">Zip</label>
              <input {...f('locationZip')} placeholder="18336" />
            </div>
          </div>

          <div style={{ fontSize:12,color:'var(--text-soft)',marginBottom:20,lineHeight:1.6 }}>
            By creating an account you agree to our{' '}
            <a style={{ color:'var(--cherry)',cursor:'pointer' }}>Terms of Service</a> and{' '}
            <a style={{ color:'var(--cherry)',cursor:'pointer' }}>Privacy Policy</a>.
          </div>

          <button type="submit" className="btn btn-cherry btn-full btn-lg" disabled={loading}>
            {loading ? <><span className="spinner"/> Creating account...</> : 'Create free account'}
          </button>
        </form>

        <SocialLogin
          onSuccess={(data) => {
            localStorage.setItem('accessToken', data.accessToken);
            updateUser(data.user);
            navigate(searchParams.get('redirect') || '/dashboard');
          }}
          onError={(msg) => setErrors({ submit: msg })}
        />

        <div style={{ textAlign:'center',marginTop:20,fontSize:14,color:'var(--text-soft)' }}>
          Already have an account?{' '}
          <a style={{ color:'var(--cherry)',fontWeight:600,cursor:'pointer' }} onClick={()=>{const redirect=searchParams.get('redirect');navigate(redirect?`/login?redirect=${encodeURIComponent(redirect)}`:'/login')}}>Sign in</a>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  ForgotPasswordPage.jsx + ResetPasswordPage.jsx
// ══════════════════════════════════════════════════════════════════
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent]   = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await API.post('/auth/forgot-password', { email }); setSent(true); }
    catch {} finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'calc(100vh - 68px)',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)',padding:24 }}>
      <div style={{ background:'white',borderRadius:20,padding:48,width:'100%',maxWidth:440,boxShadow:'var(--shadow-lg)',textAlign:'center' }}>
        <div style={{ fontSize:36,marginBottom:16 }}>🔑</div>
        <h1 style={{ fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,marginBottom:8 }}>Reset your password</h1>
        {sent ? (
          <div>
            <div className="alert alert-success" style={{ marginBottom:20 }}>✅ Check your email for a reset link</div>
            <button className="btn btn-ghost" onClick={()=>navigate('/login')}>Back to login</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p style={{ fontSize:14,color:'var(--text-soft)',marginBottom:20 }}>Enter your email and we'll send you a reset link.</p>
            <div className="form-group" style={{ textAlign:'left' }}>
              <label className="form-label">Email</label>
              <input className="form-input" type="email" placeholder="you@example.com" value={email} onChange={e=>setEmail(e.target.value)} required />
            </div>
            <button type="submit" className="btn btn-cherry btn-full" disabled={loading}>
              {loading ? <><span className="spinner"/> Sending...</> : 'Send reset link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [form, setForm] = useState({ password:'', confirm:'' });
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirm) { setError("Passwords don't match"); return; }
    if (!token) { setError('Invalid reset link — please request a new one'); return; }
    setLoading(true);
    try {
      await API.post('/auth/reset-password', { token, password: form.password });
      setDone(true);
    } catch (err) { setError(err.response?.data?.error || 'Reset failed — the link may have expired'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'calc(100vh - 68px)',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)',padding:24 }}>
      <div style={{ background:'white',borderRadius:20,padding:48,width:'100%',maxWidth:440,boxShadow:'var(--shadow-lg)',textAlign:'center' }}>
        <div style={{ fontSize:36,marginBottom:16 }}>🔒</div>
        <h1 style={{ fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,marginBottom:20 }}>Set new password</h1>

        {!token && !done && (
          <div>
            <div className="alert alert-error" style={{ marginBottom:20 }}>Invalid or missing reset link. Please request a new one.</div>
            <button className="btn btn-cherry" onClick={()=>navigate('/forgot-password')}>Request new link</button>
          </div>
        )}

        {token && done ? (
          <div>
            <div className="alert alert-success" style={{ marginBottom:20 }}>✅ Password updated! You can now log in.</div>
            <button className="btn btn-cherry" onClick={()=>navigate('/login')}>Go to login</button>
          </div>
        ) : token && (
          <form onSubmit={handleSubmit} style={{ textAlign:'left' }}>
            {error && <div className="alert alert-error" style={{ marginBottom:14 }}>{error}</div>}
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" placeholder="Min 8 characters" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" placeholder="Repeat password" value={form.confirm} onChange={e=>setForm(p=>({...p,confirm:e.target.value}))} required />
            </div>
            <button type="submit" className="btn btn-cherry btn-full" disabled={loading}>
              {loading ? <><span className="spinner"/> Updating...</> : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
